import * as R from 'ramda';
import XLSX from 'xlsx';
import Papa from 'papaparse';
import toNumber from 'lodash/toNumber';
import forIn from 'lodash/forIn';
import DOMPurify from 'dompurify';

import map from 'lodash/map';
import filter from 'lodash/filter';
import mapValues from 'lodash/mapValues';
import valuesObj from 'lodash/values';

import { round6 } from './math.js';
import { isLocalhost, isProduction } from './env';

if (!FileReader.prototype.readAsBinaryString) {
  FileReader.prototype.readAsBinaryString = function (data) {
    const reader = new FileReader();

    reader.onload = () => {
      const bytes = new Uint8Array(reader.result);
      const length = bytes.byteLength;
      let binary = '';

      for (let i = 0; i < length; i += 1) {
        binary += String.fromCharCode(bytes[i]);
      }

      this.onload({ target: { result: binary } });
    };

    reader.readAsArrayBuffer(data);
  };
}

const papaParseOptions = {
  skipEmptyLines: true,
  header: true
};

const getFileExtension = file => file.name.split('.').pop().toLowerCase();

const getRows = ({ data }) =>
  R.map(
    row =>
      R.compose(
        R.mergeAll,
        R.compose(
          R.map(key => {
            const value = row[key];
            const parsedValue = value.trim() ? toNumber(value.toString().replace(/,/g, '')) : NaN;
            const valueToSet = isNaN(parsedValue) ? value : parsedValue;

            return R.objOf(key, valueToSet);
          }),
          R.keys
        )
      )(row),
    data
  );

const parseXLS = (file, callback) => {
  const reader = new FileReader();

  reader.onload = ({ target }) => {
    const content = XLSX.read(target.result, { type: 'binary' });
    const csv = XLSX.utils.sheet_to_csv(content.Sheets.EXCEL_DATA);
    callback(getRows(Papa.parse(csv, papaParseOptions)));
  };

  reader.readAsBinaryString(file);
};

const parseCSV = (file, callback) => {
  const complete = R.pipe(getRows, callback);
  Papa.parse(file, { ...papaParseOptions, complete });
};

export const parseDatasetFile = (file, callback) => {
  const extension = getFileExtension(file);

  let processor = () => {
    // eslint-disable-next-line
    alert("Invalid file!");
  };

  if (extension === 'xls' || extension === 'xlsx') {
    processor = parseXLS;
  } else if (extension === 'csv') {
    processor = parseCSV;
  }

  processor(file, callback);
};

export const parseDatasetURL = (url, callback, fileReadErrorCallback) => {
  const complete = R.pipe(getRows, callback);
  Papa.parse(url, {
    ...papaParseOptions,
    download: true,
    complete,
    error: fileReadErrorCallback
  });
};

export const parseDatasetString = csvStr => {
  const rows = Papa.parse(csvStr, papaParseOptions);
  return getRows(rows);
};

export const defineDatasetVarTypes = dataSet => {
  let notEmptyDataSet = filter(dataSet, datasetRow => valuesObj(datasetRow).every(param => param !== 'empty'));

  if (!notEmptyDataSet.length) {
    notEmptyDataSet = map(dataSet, dataSetRow => mapValues(dataSetRow, value => value === 'empty' ? 1 : value));
  }
  const keysArr = Object.keys(notEmptyDataSet[0]);
  const valuesArr = keysArr.map(el => notEmptyDataSet.some(data => typeof data[el] !== 'number' && !!data[el].length));
  const datasetVarTypes = Object.fromEntries(keysArr.map((_, i) => [keysArr[i], valuesArr[i] ? 'categorical' : 'numerical']));

  return datasetVarTypes;
};

export const getParameterType = value =>
  typeof value === 'number' ? 'numerical' : 'categorical';

export const getParameterTypeByDefault = defaultType => (_, k) => defaultType[k];

export const getFilterByType = (type, options) => {
  switch (type) {
    case 'LT':
      return R.lt(R.__, options.x);
    case 'LTE':
      return R.lte(R.__, options.x);
    case 'GT':
      return R.gt(R.__, options.x);
    case 'GTE':
      return R.gte(R.__, options.x);
    case 'EQLNUM':
      return R.equals(!isNaN(options.x) ? +options.x : options.x);
    case 'EQLCAT':
      return value => {
        return R.equals(options.x)(`${value}`);
      };
    case 'BTW':
      return value => value > options.x && value < options.y;
    case 'NEQL':
      return R.complement(R.equals(options.x));
    default:
      return null;
  }
};

// We need to change 0.000000 to < 0.00001 in order to get statistically correct value

export const getPValue = value =>
  round6(value) ? round6(value) : '< 0.00001';

export const pageSizeForDataSet = [10, 20, 50, 100];

export const getProportion = (levelAmount, totalAmount) =>
  parseFloat((levelAmount / totalAmount).toFixed(4));

export const generateKey = pre => `${pre}_${new Date().getTime()}`;

export const removeEmptyValuesFromDataset = (dataset, firstParamName, secondParamName) => {
  const datasetWithoutEmptyFields = [];
  forIn(dataset, datasetRow => {
    if (datasetRow[firstParamName] === 'empty' || datasetRow[firstParamName] === '' || datasetRow[secondParamName] === 'empty' || datasetRow[secondParamName] === '') {
      return;
    }
    datasetWithoutEmptyFields.push(datasetRow);
  });

  return datasetWithoutEmptyFields;
};

export const getHubFilePath = () => {
  if (isLocalhost()) {
    return `${process.env.PUBLIC_URL}/cengageDatasets/objects.json`;
  }

  if (isProduction()) {
    return '/statistics1/dataset_hub/objects.txt';
  }

  return '/statistics1/dataset_hub/objects.txt';
};

export const getHubCSVFilePath = csvFilePath => {
  if (isLocalhost()) {
    return `${process.env.PUBLIC_URL}/cengageDatasets/${csvFilePath}`;
  }

  if (isProduction()) {
    return `/statistics1/dataset_hub/${csvFilePath}`;
  }

  return `/statistics1/dataset_hub/${csvFilePath}`;
};

const allowListConfig = {
  ALLOWED_TAGS: [
  ]
};

DOMPurify.setConfig(allowListConfig);

export const sanitizeAllHTML = htmlString => {
  return DOMPurify.sanitize(htmlString);
};
